import { useEffect, useMemo, useRef } from "react";

import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";

import { useAppSelector } from "@app/hooks";

import { AssistantMessageType } from "@components/AssistantChat/type";
import { MessageType } from "@components/Conversation/type";

import { useGawainAssistant } from "@contexts/GawainAssistantContext";

import ChatItem from "./ChatItem";
import { useFetchMessages } from "./hook";

const HistoryChat = () => {
  const { currentSession } = useGawainAssistant();
  const { user } = useAppSelector((state) => state.auth.userInfo);
  const thisYearStart = dayjs().startOf("year");
  const todayStart = dayjs().startOf("day");
  const yesterDayStart = dayjs(todayStart).add(-1, "day");
  const scrollRef = useRef<HTMLDivElement>(null);
  const { messages, loading, fetchMessages, isFetched } =
    useFetchMessages(currentSession);

  useEffect(() => {
    if (currentSession && !isFetched) {
      fetchMessages();
    }
  }, [currentSession, fetchMessages, isFetched]);

  useEffect(() => {
    //  auto scroll to last item
    const lastElement = scrollRef.current;
    lastElement?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const renderGroupDate = (date?: number | { seconds: number }) => {
    if (!date) {
      return "";
    }

    let dateFormat = "ddd, DD MMM, YYYY";
    const dateData = dayjs(typeof date === "number" ? date : date?.seconds * 1000);
    if (dateData.diff(thisYearStart, "second") >= 0) {
      dateFormat = "ddd, DD MMM";
    }
    if (dateData.diff(yesterDayStart, "second") >= 0) {
      dateFormat = "Yesterday";
    }
    if (dateData.diff(todayStart, "second") >= 0) {
      dateFormat = "Today";
    }

    return ["Yesterday", "Today"].includes(dateFormat)
      ? dateFormat
      : dayjs(dateData).format(dateFormat);
  };

  const dataMessage = useMemo(() => {
    return (
      messages?.map((message: AssistantMessageType) => ({
        ...message,
        text:
          typeof message.content === "string"
            ? message.content
            : message.content?.[0]?.text || "",
        position: message.role === "assistant" ? "right" : "left",
        groupDate: renderGroupDate(message.date as number),
        title: message.title || "",
        avatar: message.avatar || "",
      })) || []
    );
  }, [messages, user?.id]) as MessageType[];

  return (
    <Stack gap={2}>
      <Box ref={scrollRef} flexGrow={1} p={2} height={"100%"} className="history-chat">
        {dataMessage?.length == 0 && !loading && (
          <Typography textAlign={"center"}>
            Talk to Gawain about what you need—AI matching is based on your post. The more
            accurate the details you share, the better freelancer or AI we can match you
            with.
          </Typography>
        )}
        {loading ? (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          dataMessage.map((message, idx) => {
            const prevMsg = dataMessage?.[idx - 1];
            const groupMark = (
              <Typography textAlign={"center"} color={"#00000099"} fontSize={"10px"}>
                {prevMsg?.groupDate !== message?.groupDate && message?.groupDate}
              </Typography>
            );
            return (
              <Box key={message?.id} className="msg-item">
                {groupMark}
                <ChatItem {...message} />
              </Box>
            );
          })
        )}
      </Box>
    </Stack>
  );
};
export default HistoryChat;
